import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"
import tippy from "tippy.js"

// Global Search Controller - keyboard/mouse navigable dropdown search.
export default class extends Controller {
  static targets = ["input", "results", "dropdown"]

  static values = {
    url: String,
    minLength: { type: Number, default: 2 },
    debounce: { type: Number, default: 300 }
  }

  static BORDER_RADIUS_DURATION = 10
  static DROPDOWN_HEIGHT_DURATION = 200

  connect() {
    logger.log("GlobalSearch controller connected")
    this.selectedIndex = -1
    this.results = []
    this.abortController = null

    this.handleKeydown = this.handleKeydown.bind(this)
    this.inputTarget.addEventListener('keydown', this.handleKeydown)

    // Capture phase so we run before any handler that might stopPropagation on the bubble.
    this.handleGlobalKeydown = this.handleGlobalKeydown.bind(this)
    document.addEventListener('keydown', this.handleGlobalKeydown, true)

    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.handleClickOutside)
  }

  disconnect() {
    this.inputTarget.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('keydown', this.handleGlobalKeydown, true)
    document.removeEventListener('click', this.handleClickOutside)
    if (this.abortController) this.abortController.abort()
    if (this.tippyInstances) this.tippyInstances.forEach(i => i.destroy())
  }

  handleGlobalKeydown(event) {
    // Lowercased so Shift+Cmd+F or capital F (some IMEs) still matches.
    const isFindShortcut = (event.metaKey || event.ctrlKey) && event.key?.toLowerCase() === 'f'
    if (!isFindShortcut) return
    if (document.activeElement === this.inputTarget) return
    event.preventDefault()
    // Routes through expandMobile so the mobile overlay opens before focus.
    this.expandMobile()
    this.inputTarget.select()
  }

  onInput(event) {
    const query = event.target.value.trim()
    clearTimeout(this.searchTimeout)

    if (query.length < this.minLengthValue) {
      this.hideDropdown()
      return
    }

    this.searchTimeout = setTimeout(() => this.search(query), this.debounceValue)
  }

  async search(query) {
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()
    this.currentQuery = query

    try {
      this.showLoading()
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('q', query)

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: this.abortController.signal
      })

      if (!response.ok) throw new Error('Search failed')

      const data = await response.json()
      this.results = this.flattenResults(data)
      this.renderResults(data)
      this.animateToContentHeight()
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error)
        this.showError()
      }
    }
  }

  flattenResults(data) {
    const flat = []
    data.forEach(group => {
      group.records.forEach(record => {
        flat.push({ ...record, icon: group.icon, modelName: group.display_name })
      })
    })
    return flat
  }

  renderResults(data) {
    const i18n = window.I18n?.global_search || {}

    if (data.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="global-search-no-results p-3 text-muted text-center">
          ${i18n.no_results || 'לא נמצאו תוצאות'}
        </div>
      `
      return
    }

    let html = ''
    data.forEach(group => {
      if (group.records.length === 0) return
      html += `
        <div class="global-search-group">
          <div class="global-search-group-header px-3 py-2 text-muted small">
            <i class="fas fa-${group.icon} me-1"></i>
            ${group.display_name}
          </div>
          ${group.records.map(record => this.renderResult(record, group.icon)).join('')}
        </div>
      `
    })

    this.resultsTarget.innerHTML = html
    this.addTooltipsToClippedResults()
  }

  addTooltipsToClippedResults() {
    if (this.tippyInstances) this.tippyInstances.forEach(i => i.destroy())
    this.tippyInstances = []

    const resultLinks = this.resultsTarget.querySelectorAll('.global-search-result')
    resultLinks.forEach(linkEl => {
      const titleEl = linkEl.querySelector('.global-search-result-title')
      const subtitleEl = linkEl.querySelector('.global-search-result-subtitle')
      const titleClipped = titleEl && titleEl.scrollWidth > titleEl.clientWidth
      const subtitleClipped = subtitleEl && subtitleEl.scrollWidth > subtitleEl.clientWidth

      if (titleClipped || subtitleClipped) {
        const instance = tippy(linkEl, {
          content: linkEl.dataset.fullText,
          placement: 'top',
          delay: [300, 0],
          arrow: true,
          theme: 'light-border',
          appendTo: document.body
        })
        this.tippyInstances.push(instance)
      }
    })
  }

  renderResult(record, icon) {
    if (!record.path) return ''
    const highlightedTitle = this.highlightMatches(record.title)
    const highlightedSubtitle = record.subtitle ? this.highlightMatches(record.subtitle) : ''
    const subtitleHtml = highlightedSubtitle
      ? `<span class="global-search-result-subtitle text-muted small">${highlightedSubtitle}</span>`
      : ''
    const fullText = record.subtitle ? `${record.title} - ${record.subtitle}` : record.title

    return `
      <a href="${record.path}"
         class="global-search-result d-flex flex-column px-3 py-2 text-decoration-none"
         data-action="click->global-search#selectResult mouseenter->global-search#highlightResult"
         data-global-search-path-param="${record.path}"
         data-full-text="${this.escapeHtml(fullText)}">
        <span class="global-search-result-title">${highlightedTitle}</span>
        ${subtitleHtml}
      </a>
    `
  }

  highlightMatches(text) {
    if (!this.currentQuery || !text) return this.escapeHtml(text)
    const escaped = this.escapeHtml(text)
    const query = this.currentQuery.toLowerCase()
    const queryWords = query.split(/\s+/).filter(w => w.length > 0)
    if (queryWords.length === 0) return escaped
    const patterns = queryWords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    const regex = new RegExp(`(${patterns.join('|')})`, 'gi')
    return escaped.replace(regex, '<mark>$1</mark>')
  }

  handleKeydown(event) {
    // Escape always works (even when dropdown is hidden) so an empty
    // mobile-expanded search can still be dismissed with ESC. Also
    // clears the query/results so reopening starts from a clean slate.
    if (event.key === 'Escape') {
      this.clearSearch()
      this.hideDropdown()
      this.inputTarget.blur()
      if (this.element.classList.contains('mobile-expanded')) {
        this.collapseMobile()
      }
      return
    }

    // Other navigation keys require the dropdown to be open
    if (!this.hasDropdownTarget || this.dropdownTarget.classList.contains('d-none')) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.navigateResults(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.navigateResults(-1)
        break
      case 'Enter':
        event.preventDefault()
        this.selectCurrentResult()
        break
    }
  }

  // Expand collapsed mobile search and focus input.
  // On desktop the input is already visible — just focus.
  expandMobile() {
    if (window.matchMedia('(max-width: 767.98px)').matches) {
      this.element.classList.add('mobile-expanded')
      // Force reflow so display change is applied before focusing,
      // and keep focus() synchronous to preserve the user-gesture chain
      // (required on mobile Safari for the soft keyboard to appear)
      void this.inputTarget.offsetHeight
    }
    this.inputTarget.focus()
  }

  // Collapse expanded mobile search
  collapseMobile() {
    this.hideDropdown()
    this.element.classList.remove('mobile-expanded')
  }

  // Reset query and cached results so the next open starts clean.
  clearSearch() {
    if (this.inputTarget.value) {
      this.inputTarget.value = ''
    }
    this.results = []
    this.selectedIndex = -1
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ''
    }
  }

  // Collapse on blur - delayed so dropdown clicks can still register
  onBlur() {
    setTimeout(() => {
      if (!this.element.contains(document.activeElement)) {
        this.collapseMobile()
      }
    }, 150)
  }

  navigateResults(direction) {
    const items = this.resultsTarget.querySelectorAll('.global-search-result')
    if (items.length === 0) return

    if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
      items[this.selectedIndex].classList.remove('active')
    }

    this.selectedIndex += direction
    if (this.selectedIndex < 0) this.selectedIndex = items.length - 1
    if (this.selectedIndex >= items.length) this.selectedIndex = 0

    const selected = items[this.selectedIndex]
    selected.classList.add('active')
    selected.scrollIntoView({ block: 'nearest' })
  }

  selectCurrentResult() {
    if (this.selectedIndex >= 0 && this.results[this.selectedIndex]) {
      const result = this.results[this.selectedIndex]
      if (result.path) {
        Turbo.cache.clear()
        Turbo.visit(result.path)
        this.hideDropdown()
      }
    }
  }

  highlightResult(event) {
    const items = this.resultsTarget.querySelectorAll('.global-search-result')
    items.forEach((item, idx) => {
      item.classList.remove('active')
      if (item === event.currentTarget) {
        this.selectedIndex = idx
        item.classList.add('active')
      }
    })
  }

  selectResult(event) {
    event.preventDefault()
    const path = event.currentTarget.dataset.globalSearchPathParam
    if (path) {
      Turbo.cache.clear()
      Turbo.visit(path)
      this.hideDropdown()
    }
  }

  isDropdownVisible() {
    return this.hasDropdownTarget &&
      (!this.dropdownTarget.classList.contains('d-none') || this.isShowingDropdown)
  }

  showDropdown() {
    if (this.hasDropdownTarget && !this.isShowingDropdown && !this.isDropdownVisible()) {
      this.isShowingDropdown = true
      this.element.classList.add('dropdown-open')

      setTimeout(() => {
        this.dropdownTarget.classList.remove('d-none')
        this.dropdownTarget.style.height = '0px'
        this.dropdownTarget.offsetHeight
        this.animateToContentHeight()
        this.selectedIndex = -1
        this.isShowingDropdown = false
      }, this.constructor.BORDER_RADIUS_DURATION)
    }
  }

  hideDropdown() {
    this.isShowingDropdown = false

    if (this.hasDropdownTarget && !this.dropdownTarget.classList.contains('d-none')) {
      this.dropdownTarget.classList.remove('can-scroll')
      this.dropdownTarget.style.height = '0px'
      this.selectedIndex = -1

      setTimeout(() => {
        this.dropdownTarget.classList.add('d-none')
        this.dropdownTarget.style.height = ''
        this.element.classList.remove('dropdown-open')
      }, this.constructor.DROPDOWN_HEIGHT_DURATION)
    } else if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add('d-none')
      this.element.classList.remove('dropdown-open')
      this.selectedIndex = -1
    }
  }

  animateToContentHeight() {
    if (this.hasDropdownTarget) {
      const naturalHeight = this.resultsTarget.scrollHeight
      const maxHeight = 400
      const targetHeight = Math.min(naturalHeight, maxHeight)
      this.dropdownTarget.style.height = `${targetHeight}px`

      if (naturalHeight > maxHeight) {
        setTimeout(() => this.dropdownTarget.classList.add('can-scroll'), 200)
      }
    }
  }

  showLoading() {
    const i18n = window.I18n?.global_search || {}
    this.resultsTarget.innerHTML = `
      <div class="global-search-loading p-3 text-center">
        <i class="fas fa-spinner fa-spin me-2"></i>
        ${i18n.loading || 'טוען...'}
      </div>
    `
    if (this.isDropdownVisible()) {
      this.animateToContentHeight()
    } else {
      this.showDropdown()
    }
  }

  showError() {
    const i18n = window.I18n?.global_search || {}
    this.resultsTarget.innerHTML = `
      <div class="global-search-error p-3 text-danger text-center">
        ${i18n.error || 'שגיאת חיפוש'}
      </div>
    `
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
      if (this.element.classList.contains('mobile-expanded')) {
        this.element.classList.remove('mobile-expanded')
      }
    }
  }

  onFocus() {
    if (this.results.length > 0) this.showDropdown()
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
